import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, fmtPrice } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Truck, PackageCheck, ShieldAlert } from "lucide-react";
import { toast } from "sonner";

export default function Admin() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drafts, setDrafts] = useState({});
  const [saving, setSaving] = useState(null);

  useEffect(() => {
    if (user === null) { navigate("/compte"); return; }
    if (user && !user.is_admin) return;
    if (user?.is_admin) {
      axios.get(`${API}/admin/orders`)
        .then((r) => {
          setOrders(r.data.orders);
          const d = {};
          r.data.orders.forEach((o) => { d[o.session_id] = { tracking_number: o.tracking_number || "", carrier: o.carrier || "" }; });
          setDrafts(d);
        })
        .catch(() => {})
        .finally(() => setLoading(false));
    }
  }, [user, navigate]);

  if (user === undefined || (user && user.is_admin && loading)) {
    return <div className="flex min-h-[50vh] items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-blue-600" /></div>;
  }

  if (user && !user.is_admin) {
    return (
      <div className="mx-auto flex max-w-xl flex-col items-center px-6 py-24 text-center text-slate-500">
        <ShieldAlert className="h-14 w-14 text-slate-300" />
        <h1 className="mt-4 font-head text-2xl font-extrabold text-slate-900">Accès réservé</h1>
        <p className="mt-2">Cette page est réservée à l'administrateur de la boutique.</p>
      </div>
    );
  }

  const save = async (sid) => {
    const d = drafts[sid];
    if (!d?.tracking_number?.trim()) { toast.error("Saisissez un numéro de suivi"); return; }
    setSaving(sid);
    try {
      await axios.put(`${API}/admin/orders/${sid}/tracking`, d);
      setOrders((prev) => prev.map((o) => (o.session_id === sid ? { ...o, ...d, shipping_status: "expédiée" } : o)));
      toast.success("Suivi enregistré");
    } catch (e) {
      toast.error("Erreur lors de l'enregistrement");
    } finally { setSaving(null); }
  };

  return (
    <div className="mx-auto max-w-5xl px-4 py-12 md:px-6">
      <h1 className="flex items-center gap-2 font-head text-3xl font-extrabold text-slate-900">
        <Truck className="h-7 w-7 text-blue-600" /> Gestion des expéditions
      </h1>
      <p className="mt-1 text-sm text-slate-500">Ajoutez un numéro de suivi à chaque commande payée. Il apparaîtra dans l'espace client.</p>

      {orders.length === 0 ? (
        <div className="mt-10 rounded-xl border border-dashed border-slate-300 p-10 text-center text-slate-400">Aucune commande payée pour le moment.</div>
      ) : (
        <div className="mt-8 space-y-4">
          {orders.map((o) => (
            <div key={o.session_id} className="rounded-xl border border-slate-200 p-5" data-testid={`admin-order-${o.session_id}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <p className="text-sm text-slate-400">{new Date(o.created_at).toLocaleString("fr-FR")} · {o.customer_email || "client"}</p>
                  <p className="font-head text-lg font-bold text-slate-900">{fmtPrice(o.amount)} · {(o.items || []).length} article(s)</p>
                </div>
                {o.shipping_status === "expédiée" ? (
                  <span className="inline-flex items-center gap-1 rounded-full bg-green-50 px-3 py-1 text-xs font-semibold text-green-700"><PackageCheck className="h-4 w-4" /> Expédiée</span>
                ) : (
                  <span className="rounded-full bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-700">À expédier</span>
                )}
              </div>
              <div className="mt-4 flex flex-col gap-3 sm:flex-row">
                <Input
                  placeholder="Transporteur (ex. Colissimo)"
                  value={drafts[o.session_id]?.carrier || ""}
                  onChange={(e) => setDrafts((p) => ({ ...p, [o.session_id]: { ...p[o.session_id], carrier: e.target.value } }))}
                  data-testid={`carrier-${o.session_id}`}
                  className="sm:max-w-[220px]"
                />
                <Input
                  placeholder="Numéro de suivi"
                  value={drafts[o.session_id]?.tracking_number || ""}
                  onChange={(e) => setDrafts((p) => ({ ...p, [o.session_id]: { ...p[o.session_id], tracking_number: e.target.value } }))}
                  data-testid={`tracking-${o.session_id}`}
                />
                <Button onClick={() => save(o.session_id)} disabled={saving === o.session_id} data-testid={`save-tracking-${o.session_id}`} className="rounded-full bg-blue-600 text-white hover:bg-blue-700">
                  {saving === o.session_id && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Enregistrer
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
